<template>
	<BaseModal class="drawer-redirect-modal" data-context="Web to Mobile Redirect Modal" @modal-close="close">
		<div v-reveal:fade-up class="module px-100 pt-150 pb-600 text-align-center">
			<figure class="app-download-prompt align-items-center display-flex mx-0 my-0 text-align-left">
				<img :src="fundriseAppLogo" alt="Fundrise app logo" class="modal-app-icon float-left mr-100" />
				<figcaption>
					<h3 class="heading-5">{{ props.headerText }}</h3>
					<p class="mt-25">{{ props.bodyText }}</p>
				</figcaption>
			</figure>

			<BaseLink
				:link="{
					href: `${deviceProps.path}?utm_source=fundrise&utm_campaign=mobile_drawer&utm_medium=website`,
					target: '_blank'
				}"
			>
				<img
					class="download-app-image display-inline-block mt-150"
					:src="deviceProps.icon"
					alt="Download app icon"
				/>
			</BaseLink>

			<p class="mt-50 mb-200">
				<BaseLink :link="props.continueLink" @click="close" />
			</p>
		</div>
	</BaseModal>
</template>

<script setup lang="ts">
import { appCloudfrontPath } from '@constants';
import { computed } from 'vue';
import { isIphone } from '@utils/client';
import { Link } from 'types/layout';

interface Props {
	continueLink?: Link;
	headerText?: string;
	bodyText?: string;
}

const props = withDefaults(defineProps<Props>(), {
	continueLink: () => ({ text: 'No thanks, continue on mobile browser', router: `get-started` }),
	headerText: 'Continue on the Fundrise app?',
	bodyText: 'Signing up is easier on the app.'
});

interface Emits {
	(e: 'modal-close'): void;
}

const emit = defineEmits<Emits>();

const fundriseAppLogo = `${appCloudfrontPath}/interface/logo-glyph-white.svg`;

const googlePlayIcon = `${appCloudfrontPath}/images/google-play-badge-cropped.png`;
const googlePlayDownloadPath = `${import.meta.env.VITE_AVATAR_URL}/play-store-redirect`;

const appleStoreIcon = `${appCloudfrontPath}/images/Download_on_the_App_Store_Badge_US-UK_blk_092917.png`;
const appleStoreDownloadPath = `${import.meta.env.VITE_AVATAR_URL}/app-store-redirect`;

const deviceProps = computed((): Record<string, string> => {
	const iOSPaths = {
		path: appleStoreDownloadPath,
		icon: appleStoreIcon
	};
	const AndroidPaths = {
		path: googlePlayDownloadPath,
		icon: googlePlayIcon
	};
	return isIphone() ? iOSPaths : AndroidPaths;
});

function close(): void {
	emit('modal-close');
}

function setup(): void {
	const body: HTMLCollectionOf<HTMLBodyElement> = document.getElementsByTagName('body');
	body[0].style.overflowY = 'auto';
}

setup();
</script>

<style lang="scss" scoped>
@use '../../styles/constants/colors' as *;
@use '../../styles/constants/layout' as *;

.drawer-redirect-modal {
	.app-download-prompt {
		width: fit-content;
	}

	.modal-app-icon {
		background-color: token('brand-primary');
		border-radius: 10px;
		height: 60px;
		padding: 0.5rem;
		width: 60px;
	}

	.download-app-image {
		max-width: 165px;
	}

	@media screen and (max-width: $md-start - 1) {
		.module {
			border-radius: 8px 8px 0 0;
			bottom: 0;
			margin-left: -1rem;
			position: absolute;
			width: 100%;
		}
	}
}
</style>
